/**
 * MobileVideoPlayer — full-screen video overlay with WhatsApp-style
 * gestures. Opens fullscreen on tap, closes on swipe-down or back button,
 * remembers playback position in localStorage, supports playback-speed
 * picker.
 *
 * Uses the existing /api/files/{id}/stream endpoint (which prefers the
 * browser-safe playback preview when available).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { X, Download, Gauge } from "lucide-react";
import api, { API } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { logger } from "@/lib/logger";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const POSITION_KEY = (id) => `sf_vid_pos_${id}`;

export default function MobileVideoPlayer({ file, open, onClose }) {
  const [streamUrl, setStreamUrl] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const videoRef = useRef(null);
  const touchStartY = useRef(null);
  const [dragY, setDragY] = useState(0);

  // Load stream token + poster URL when the player opens.
  useEffect(() => {
    if (!open || !file?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setStreamUrl(null);
    setPosterUrl(null);
    (async () => {
      try {
        const { data } = await api.get(`/files/${file.id}/preview-url`);
        if (cancelled) return;
        setStreamUrl(data.url || "");
        // Poster shares the same JWT (same file, same typ). Reuse the
        // token from the stream URL — cheap and avoids a second RTT.
        try {
          const u = new URL(data.url, window.location.origin);
          const t = u.searchParams.get("t");
          if (t) setPosterUrl(`${API}/files/${file.id}/poster.jpg?t=${t}`);
        } catch { /* ignore */ }
      } catch (e) {
        logger.error("MobileVideoPlayer load failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, file?.id]);

  // Resume-position + save-position wiring.
  const onLoadedMeta = useCallback(() => {
    const v = videoRef.current;
    if (!v || !file?.id) return;
    try {
      const saved = localStorage.getItem(POSITION_KEY(file.id));
      if (saved) {
        const t = parseFloat(saved);
        // Only resume if we're at least 5 s in and not near the end.
        if (Number.isFinite(t) && t > 5 && (!v.duration || t < v.duration - 5)) {
          v.currentTime = t;
        }
      }
    } catch { /* ignore */ }
  }, [file?.id]);

  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || !file?.id) return;
    try {
      localStorage.setItem(POSITION_KEY(file.id), String(v.currentTime));
    } catch { /* ignore */ }
  }, [file?.id]);

  // Swipe-down to close (plain touch events, no framer dependency).
  const onTouchStart = (e) => {
    if (e.touches?.length !== 1) return;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e) => {
    if (touchStartY.current == null) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0) setDragY(dy);
  };
  const onTouchEnd = () => {
    if (dragY > 120) {
      onClose();
    }
    touchStartY.current = null;
    setDragY(0);
  };

  // ESC / hardware back → close.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = speed;
  }, [speed]);

  if (!open || !file) return null;
  const durationLabel = file.duration_label || "";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col select-none"
      style={{
        transform: `translateY(${dragY}px)`,
        transition: dragY === 0 ? "transform 0.25s ease-out" : "none",
        opacity: dragY > 0 ? Math.max(0.6, 1 - dragY / 500) : 1,
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid="mobile-video-player"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-3 bg-black/40 backdrop-blur">
        <button
          onClick={onClose}
          className="p-2 -ml-2 rounded-full text-white hover:bg-white/10 active:bg-white/20"
          aria-label="Close"
          data-testid="mobile-video-close"
        >
          <X size={22} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-medium truncate">{file.original_filename}</div>
          <div className="text-white/60 text-[10px]">
            {(file.size / 1024 / 1024).toFixed(1)} MB
            {durationLabel && ` · ${durationLabel}`}
          </div>
        </div>
        <button
          onClick={() => downloadFile(`${API}/files/${file.id}/download`, file.original_filename)}
          className="p-2 rounded-full text-white hover:bg-white/10 active:bg-white/20"
          aria-label="Download"
          data-testid="mobile-video-download"
        >
          <Download size={20} />
        </button>
      </div>

      {/* Player */}
      <div className="flex-1 flex items-center justify-center bg-black">
        {loading && (
          <div className="text-white/70 text-sm animate-pulse">Loading video…</div>
        )}
        {!loading && streamUrl && (
          <video
            ref={videoRef}
            src={streamUrl}
            poster={posterUrl || undefined}
            controls
            playsInline
            autoPlay
            preload="auto"
            className="w-full h-full object-contain"
            data-testid="mobile-video-element"
            onLoadedMetadata={onLoadedMeta}
            onTimeUpdate={onTimeUpdate}
          />
        )}
      </div>

      {/* Speed control */}
      <div className="px-4 py-3 bg-black/40 backdrop-blur flex items-center justify-between">
        <button
          onClick={() => setShowSpeed((s) => !s)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs active:bg-white/20"
          data-testid="mobile-video-speed"
        >
          <Gauge size={14} />
          {speed}×
        </button>
        <div className="text-white/50 text-[10px]">Swipe down to close</div>
      </div>

      {showSpeed && (
        <div className="absolute bottom-16 left-4 bg-neutral-900/95 rounded-lg p-2 shadow-2xl">
          {SPEEDS.map((s) => (
            <button
              key={s}
              onClick={() => { setSpeed(s); setShowSpeed(false); }}
              className={`block w-full text-left px-3 py-1.5 text-xs rounded ${
                s === speed ? "bg-teal text-black" : "text-white hover:bg-white/10"
              }`}
              data-testid={`mobile-video-speed-${s}`}
            >
              {s}× {s === 1 && "(normal)"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
